import { useState, useEffect, useRef } from 'react';
import { PHONE_1 } from '../../../components/feature/TopBar';

const WA_LINK = `https://wa.me/90${PHONE_1}?text=${encodeURIComponent('Merhaba, kampanya fiyatından yararlanmak istiyorum. Randevu almak istiyorum.\nAd Soyad:\nTelefon:\nAdres:\nTarih:\nSaat:')}`;

// Countdown target: end of current month
function getTargetDate() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
}

function useCountdown(target: Date) {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / (1000 * 60 * 60 * 24)),
      hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((diff / (1000 * 60)) % 60),
      seconds: Math.floor((diff / 1000) % 60),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  });
  return time;
}

function TimeBox({ value, label }: { value: number; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative w-16 h-16 md:w-20 md:h-20 flex items-center justify-center bg-white/10 border border-white/20 rounded-2xl overflow-hidden">
        <span
          className="text-white font-extrabold text-2xl md:text-3xl tabular-nums"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          {String(value).padStart(2, '0')}
        </span>
        {/* shine line */}
        <div className="absolute inset-x-0 top-0 h-1/2 bg-white/5 rounded-t-2xl" />
      </div>
      <span className="text-emerald-400 text-xs font-semibold uppercase tracking-widest">{label}</span>
    </div>
  );
}

const spotsLeft = 7; // static "spots left" number for urgency

export default function UrgencySection() {
  const target = useRef(getTargetDate()).current;
  const { days, hours, minutes, seconds } = useCountdown(target);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-y-0'); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleAppointment = () => {
    const el = document.querySelector('#appointment');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="w-full bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500 py-14 md:py-20 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute -top-16 -left-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
      <div className="absolute -bottom-16 -right-16 w-80 h-80 bg-black/10 rounded-full blur-2xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/5 rounded-full" />

      <div className="relative z-10 max-w-4xl mx-auto px-4">
        <div
          ref={ref}
          className="opacity-0 translate-y-8 transition-all duration-700 flex flex-col items-center text-center"
        >
          {/* Badge */}
          <div className="flex items-center gap-2 bg-white/20 border border-white/30 backdrop-blur-sm text-white text-xs font-bold px-4 py-2 rounded-full mb-6 uppercase tracking-widest">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-alarm-warning-fill animate-pulse" />
            </span>
            Sınırlı Süre Kampanyası
          </div>

          {/* Title */}
          <h2
            className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-3"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Kampanya Fiyatları
            <br />
            <span className="text-white/80">Ay Sonunda Bitiyor!</span>
          </h2>
          <p className="text-white/80 text-base md:text-lg max-w-xl mb-10">
            Kampanya fiyatlarımızdan yararlanmak için kalan süre:
          </p>

          {/* Countdown */}
          <div className="flex items-start gap-3 md:gap-5 mb-8">
            <TimeBox value={days} label="Gün" />
            <div className="text-white/60 text-3xl font-bold mt-4">:</div>
            <TimeBox value={hours} label="Saat" />
            <div className="text-white/60 text-3xl font-bold mt-4">:</div>
            <TimeBox value={minutes} label="Dakika" />
            <div className="text-white/60 text-3xl font-bold mt-4">:</div>
            <TimeBox value={seconds} label="Saniye" />
          </div>

          {/* Spots left */}
          <div className="flex items-center gap-2 bg-black/20 border border-white/20 rounded-full px-5 py-2.5 mb-10">
            <span className="w-2 h-2 flex items-center justify-center">
              <span className="block w-2 h-2 bg-red-400 rounded-full animate-ping absolute" />
              <span className="block w-2 h-2 bg-red-400 rounded-full relative" />
            </span>
            <span className="text-white font-semibold text-sm">
              Bu ay için yalnızca{' '}
              <strong className="text-yellow-300">{spotsLeft} randevu</strong>{' '}
              kaldı!
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-4 w-full max-w-md">
            <button
              onClick={handleAppointment}
              className="w-full flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-emerald-600 font-bold text-base px-8 py-4 rounded-xl transition-all hover:scale-105 shadow-xl shadow-black/20 whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-calendar-check-line" />
              </span>
              Hemen Randevu Al
            </button>
            <a
              href={WA_LINK}
              target="_blank"
              rel="nofollow noreferrer"
              className="w-full flex items-center justify-center gap-2 bg-black/20 hover:bg-black/30 border border-white/30 text-white font-bold text-base px-8 py-4 rounded-xl transition-all whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line" />
              </span>
              WhatsApp ile Yaz
            </a>
          </div>

          {/* Trust note */}
          <p className="text-white/60 text-xs mt-6 flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center">
              <i className="ri-shield-check-line" />
            </span>
            18 yıllık tecrübe • Profesyonel ekipman • Anadolu &amp; Avrupa Yakası
          </p>
        </div>
      </div>
    </section>
  );
}
